import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home,
  Calendar,
  TrendingUp,
  Award,
  Clock,
  MapPin,
  LogOut,
  User,
  Gift,
  Plus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerDashboard({ user, onLogout }) {
  const [dashboard, setDashboard] = useState(null);
  const [services, setServices] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [showBooking, setShowBooking] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [dashboardRes, servicesRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/customer/dashboard`, config),
        axios.get(`${API}/customer/services`, config),
        axios.get(`${API}/customer/sessions`, config)
      ]);

      setDashboard(dashboardRes.data);
      setServices(servicesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = (service) => {
    setSelectedService(service);
    setShowBooking(true);
    toast.success('Booking flow initiated for ' + service.name);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  const upcomingSessions = dashboard?.upcoming_sessions || [];
  const completedSessions = sessions.filter((s) => s.status === 'completed');
  const serviceImages = {
    Physio: 'https://images.pexels.com/photos/5794060/pexels-photo-5794060.jpeg',
    OT: 'https://images.pexels.com/photos/5794060/pexels-photo-5794060.jpeg',
    Speech: 'https://images.pexels.com/photos/23495757/pexels-photo-23495757.jpeg',
    Diet: 'https://images.pexels.com/photos/8844379/pexels-photo-8844379.jpeg',
    Care: 'https://images.pexels.com/photos/1077449/pexels-photo-1077449.jpeg'
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-0">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#D32F2F]">Physiomobile</h1>
              <p className="text-sm text-slate-600">Your Health Partner</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
                <p className="text-xs text-slate-500">{user.email}</p>
              </div>
              <button
                onClick={onLogout}
                data-testid="logout-btn"
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="stat-card" data-testid="care-plans-card">
                <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center mb-3">
                  <Home className="w-5 h-5 text-sky-500" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Active Care Plans</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard.active_care_plans}</p>
              </div>

              <div className="stat-card" data-testid="upcoming-card">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3">
                  <Calendar className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Upcoming</p>
                <p className="text-2xl font-bold text-slate-900">{upcomingSessions.length}</p>
              </div>

              <div className="stat-card" data-testid="total-sessions-card">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Total Sessions</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard.total_sessions}</p>
              </div>

              <div className="stat-card" data-testid="loyalty-points-card">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-3">
                  <Gift className="w-5 h-5 text-amber-500" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Loyalty Points</p>
                <p className="text-2xl font-bold text-slate-900">{dashboard.loyalty_points}</p>
              </div>
            </div>

            {/* Services */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-slate-900">Our Services</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((service) => (
                  <div
                    key={service.id}
                    data-testid={`service-${service.type}-card`}
                    className="service-card overflow-hidden"
                    onClick={() => handleBooking(service)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={serviceImages[service.type]}
                        alt={service.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <h4 className="text-xl font-bold text-white mb-1">{service.name}</h4>
                        <p className="text-sm text-white/90">{service.description}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-slate-600">
                          <span>Starting from </span>
                          <span className="font-bold text-[#D32F2F] text-lg">
                            ${Math.min(...Object.values(service.pricing))}
                          </span>
                        </div>
                        <button
                          className="btn-primary text-sm px-4 py-2"
                          data-testid={`book-${service.type}-btn`}
                        >
                          Book Now
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Sessions */}
            {upcomingSessions.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Upcoming Sessions</h3>
                <div className="space-y-4">
                  {upcomingSessions.map((session) => (
                    <div
                      key={session.id}
                      data-testid={`session-${session.id}-card`}
                      className="flex items-start space-x-4 p-4 bg-slate-50 rounded-xl"
                    >
                      <div className="w-12 h-12 rounded-full bg-[#D32F2F] flex items-center justify-center text-white font-bold flex-shrink-0">
                        {session.professional_name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{session.professional_name}</p>
                        <p className="text-sm text-slate-600">{session.service_type}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{new Date(session.date).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MapPin className="w-4 h-4" />
                            <span className="capitalize">{session.location}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-[#D32F2F]">${session.cost}</p>
                        <span className={`badge-status badge-${session.status} mt-2`}>
                          {session.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Session History</h3>
              
              {completedSessions.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No completed sessions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedSessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{session.professional_name}</p>
                        <p className="text-sm text-slate-600">{session.service_type}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(session.date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        {session.rating && (
                          <div className="flex items-center space-x-1 text-amber-500 mb-1">
                            <Award className="w-4 h-4" />
                            <span className="font-semibold">{session.rating}</span>
                          </div>
                        )}
                        <p className="text-sm font-semibold text-emerald-600">${session.cost}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Loyalty Rewards</h3>
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D32F2F] to-[#B71C1C] flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-10 h-10 text-white" />
                </div>
                <p className="text-3xl font-bold text-slate-900 mb-2">{dashboard.loyalty_points}</p>
                <p className="text-sm text-slate-600 mb-6">Points Available</p>
                <button className="btn-primary">
                  Redeem Points
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 md:hidden z-30">
        <div className="flex justify-around">
          <button
            onClick={() => setActiveTab('home')}
            data-testid="tab-home-btn"
            className={`flex flex-col items-center space-y-1 ${
              activeTab === 'home' ? 'text-[#D32F2F]' : 'text-slate-400'
            }`}
          >
            <Home className="w-6 h-6" />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            data-testid="tab-progress-btn"
            className={`flex flex-col items-center space-y-1 ${
              activeTab === 'progress' ? 'text-[#D32F2F]' : 'text-slate-400'
            }`}
          >
            <TrendingUp className="w-6 h-6" />
            <span className="text-xs font-medium">Progress</span>
          </button>
        </div>
      </div>

      {/* Booking Modal Placeholder */}
      {showBooking && selectedService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Book {selectedService.name}</h3>
            <p className="text-slate-600 mb-6">Booking form would appear here in production.</p>
            <button
              onClick={() => setShowBooking(false)}
              className="btn-primary w-full"
              data-testid="close-booking-modal-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
